import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, Outlet, useNavigate } from "react-router-dom";
import DashboardHeader from "./DashboardHeader";
import { useLotDetails } from "../../hooks/useLotData";
import Modal from "../../components/Modal";
import VideoModal from "../../components/VideoModal";
import { useWalkthrough } from "../../components/Walkthrough/WalkthroughContext";
import { AI_CHAT_VISIBILITY_EVENT, VISIBILITY_STORAGE_KEY } from "../../components/ai/Chat";
import "./Dashboard.css";

interface DateRange {
    startDate: string;
    endDate: string;
}

interface DashboardContext {
    currentRange: DateRange;
    previousRange: DateRange;
    timeframe: "day" | "week" | "month" | "year";
}

const Dashboard: React.FC = () => {
    const { lotId } = useParams<{ lotId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const [timeframe, setTimeframe] = useState<"day" | "week" | "month" | "year">("day");
    const [isLoading, setIsLoading] = useState(false);
    const [graphData, setGraphData] = useState<any[]>([]);
    const [dateRanges, setDateRanges] = useState<{
        currentRange: DateRange;
        previousRange: DateRange;
    }>({
        currentRange: { startDate: "", endDate: "" },
        previousRange: { startDate: "", endDate: "" }
    });

    // NEW: shared selectedDate state
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    // NEW: state to control if we are showing today (to allow automatic date range updates)
    const [showToday, setShowToday] = useState<boolean>(true);

    // Add states for tutorial modal
    const [showTutorialButton, setShowTutorialButton] = useState(true); // Always show the button
    const [modalOpen, setModalOpen] = useState(false);
    const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
    const { active: walkthroughActive } = useWalkthrough();

    // Hide the tutorial button while the AI chat is open so the two don't compete for the bottom edge.
    const [chatOpen, setChatOpen] = useState<boolean>(
        () => localStorage.getItem(VISIBILITY_STORAGE_KEY) === "open",
    );
    useEffect(() => {
        const handleVisibility = (event: Event) => {
            const detail = (event as CustomEvent<{ open: boolean }>).detail;
            setChatOpen(Boolean(detail?.open));
        };
        window.addEventListener(AI_CHAT_VISIBILITY_EVENT, handleVisibility);
        return () => window.removeEventListener(AI_CHAT_VISIBILITY_EVENT, handleVisibility);
    }, []);

    // Use our new cached lot details hook
    const { data: lotData, isLoading: isLotLoading } = useLotDetails(lotId || "");

    useEffect(() => {
        // If the URL is exactly /lot/{lotId}/dashboard, redirect to the last visited tab or default to 'revenue'
        if (lotId && location.pathname === `/lot/${lotId}/dashboard`) {
            const lastTab = localStorage.getItem("lastDashboardTab") || "revenue";
            navigate(`/lot/${lotId}/dashboard/${lastTab}`, { replace: true });
        }
    }, [lotId, location.pathname, navigate]);

    // Add effect to listen for graph data updates
    useEffect(() => {
        const handleGraphDataUpdate = (event: CustomEvent) => {
            console.log('Received graph data update:', event.detail.graphData);
            setGraphData(event.detail.graphData);
        };

        window.addEventListener('graphDataUpdate', handleGraphDataUpdate as EventListener);

        return () => {
            window.removeEventListener('graphDataUpdate', handleGraphDataUpdate as EventListener);
        };
    }, []);

    const handleDateRangeChange = useMemo(() => (currentRange: DateRange, previousRange: DateRange) => {
        console.log("Date range changed:", { currentRange, previousRange });
        setDateRanges({ currentRange, previousRange });
    }, []);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        ...dateRanges,
        timeframe,
        setTimeframe,
        onDateRangeChange: handleDateRangeChange,
        selectedDate,
        setSelectedDate,
        showToday,
        setShowToday,
        graphData
    }), [dateRanges, timeframe, handleDateRangeChange, selectedDate, showToday, graphData]);

    // Handle tutorial button click
    const handleTutorialClick = () => {
        setModalOpen(true);
        // Don't hide the button after watching - keep it persistent
        // setShowTutorialButton(false);
        // localStorage.setItem('hasWatchedDashboardTutorial', 'true');
    };

    const handleMouseEnter = () => {
        const timeout = setTimeout(() => {
            setShowTutorialButton(true);
        }, 3000);
        setHoverTimeout(timeout);
    };

    const handleMouseLeave = () => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            setHoverTimeout(null);
        }
        // Don't hide the button based on localStorage - keep it persistent
        // if (localStorage.getItem('hasWatchedDashboardTutorial') === 'true') {
        //     setShowTutorialButton(false);
        // }
    };

    return (
        <div>
            <DashboardHeader
                lotName={lotData?.lotName || "..."}
                isLoading={isLotLoading}
                timeframe={timeframe}
                setTimeframe={setTimeframe}
                onDateRangeChange={handleDateRangeChange}
                graphData={graphData}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                showToday={showToday}
                setShowToday={setShowToday}
            />
            {/* Key outlet by path so dashboard tabs fully remount when switching */}
            <Outlet key={location.pathname} context={contextValue} />

            {/* Tutorial Button - PERSISTENT, hidden during walkthrough */}
            <div
                className="tutorial-button-container"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '10px',
                    zIndex: 1000,
                    ...(walkthroughActive || chatOpen ? { display: 'none' } : {}),
                }}
            >
                <button
                    className={`tutorial-button ${showTutorialButton ? 'visible' : ''}`}
                    onClick={handleTutorialClick}
                >
                    <img src="/assets/play.svg" alt="Play" />
                    Watch our Tutorial Video
                </button>
            </div>

            {/* Tutorial Modal */}
            {modalOpen && (
                <VideoModal
                    isOpen
                    title="Dashboard Tutorial"
                    videoUrl="https://www.youtube.com/watch?v=V2lEswZgZEw"
                    onClose={() => setModalOpen(false)}
                />
            )}
        </div>
    );
};

export default Dashboard; 
